/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const zhTW: LocalizationResource = {
  locale: 'zh-TW',
  apiKeys: {
    action__add: '新增金鑰',
    action__search: '搜尋金鑰',
    copySecret: {
      formButtonPrimary__copyAndClose: '複製並關閉',
      formHint: '基於安全理由，我們不會允許您稍後再次查看。',
      formTitle: '立即複製您的"{{name}}" API 金鑰',
    },
    createdAndExpirationStatus__expiresOn:
      "已於 {{ createdDate | shortDate('zh-TW') }} 建立 • 將於 {{ expiresDate | longDate('zh-TW') }} 過期",
    createdAndExpirationStatus__never: "已於 {{ createdDate | shortDate('zh-TW') }} 建立 • 永不過期",
    detailsTitle__emptyRow: '找不到 API 金鑰',
    formButtonPrimary__add: '建立金鑰',
    formFieldCaption__expiration__expiresOn: '將於 {{ date }} 過期',
    formFieldCaption__expiration__never: '此金鑰將永不過期',
    formFieldOption__expiration__180d: '180 天',
    formFieldOption__expiration__1d: '1 天',
    formFieldOption__expiration__1y: '1 年',
    formFieldOption__expiration__30d: '30 天',
    formFieldOption__expiration__60d: '60 天',
    formFieldOption__expiration__7d: '7 天',
    formFieldOption__expiration__90d: '90 天',
    formFieldOption__expiration__never: '永不過期',
    formHint: '提供一個名稱來生成新的金鑰。您可以隨時撤銷它。',
    formTitle: '新增 API 金鑰',
    lastUsed__days: '{{days}} 天前',
    lastUsed__hours: '{{hours}} 小時前',
    lastUsed__minutes: '{{minutes}} 分前',
    lastUsed__months: '{{months}} 個月前',
    lastUsed__seconds: '{{seconds}} 秒前',
    lastUsed__years: '{{years}} 年前',
    menuAction__revoke: '撤銷金鑰',
    revokeConfirmation: {
      confirmationText: '撤銷',
      formButtonPrimary__revoke: '撤銷金鑰',
      formHint: '您確定要刪除此 API 金鑰嗎？',
      formTitle: '撤銷 "{{apiKeyName}}" API 金鑰？',
      inputLabel: undefined,
    },
    tableHeader__actions: undefined,
    tableHeader__lastUsed: undefined,
    tableHeader__name: undefined,
  },
  backButton: '返回',
  badge__activePlan: '啟用中',
  badge__banned: undefined,
  badge__canceledEndsAt: "已取消 • 將於 {{ date | shortDate('zh-TW') }} 到期",
  badge__currentPlan: '目前計劃',
  badge__default: '預設',
  badge__endsAt: "將於 {{ date | shortDate('zh-TW') }} 到期",
  badge__expired: '已過期',
  badge__freeTrial: '免費試用',
  badge__otherImpersonatorDevice: '其他模擬裝置',
  badge__pastDueAt: "已逾期 {{ date | shortDate('zh-TW') }}",
  badge__pastDuePlan: '已逾期',
  badge__primary: '主要',
  badge__renewsAt: "將於 {{ date | shortDate('zh-TW') }} 續期",
  badge__requiresAction: '需要操作',
  badge__startsAt: "將於 {{ date | shortDate('zh-TW') }} 開始",
  badge__thisDevice: '此設備',
  badge__trialEndsAt: "試用期將於 {{ date | shortDate('zh-TW') }} 到期",
  badge__unverified: '未驗證',
  badge__upcomingPlan: '即將生效',
  badge__userDevice: '用戶設備',
  badge__you: '您',
  billing: {
    accountCredit: '帳戶餘額',
    addPaymentMethod__label: '新增付款方式',
    alwaysFree: '永遠免費',
    annually: '每年',
    availableFeatures: '可使用功能',
    billedAnnually: '每年',
    billedAnnuallyOnly: undefined,
    billedMonthly: undefined,
    billedMonthlyOnly: '每月',
    cancelFreeTrial: '取消免費試用',
    cancelFreeTrialAccessUntil:
      "您的試用期將維持有效直到 {{ date | longDate('zh-TW') }}。在那之後，您將失去對試用期功能的存取。您不會被收取費用。",
    cancelFreeTrialTitle: '取消 {{plan}} 計劃的免費試用？',
    cancelSubscription: '取消訂閱',
    cancelSubscriptionAccessUntil:
      "您可以繼續使用 '{{plan}}' 功能直到 {{ date | longDate('zh-TW') }}，在那之後，您將失去對其的存取。",
    cancelSubscriptionNoCharge: '您不會被收取此訂閱的費用。',
    cancelSubscriptionPastDue: '您的訂閱將立即結束，您將失去所有計劃功能的存取。您將在下一次訂閱時被要求支付逾期金額。',
    cancelSubscriptionTitle: '取消 {{plan}} 訂閱？',
    cannotSubscribeMonthly: '您無法每月支付訂閱此計劃。要訂閱此計劃，您需要選擇每年支付。',
    cannotSubscribeUnrecoverable: '您無法訂閱此計劃。您的現有訂閱比此計劃更昂貴。',
    checkout: {
      description__paymentSuccessful: '您的付款已成功。',
      description__subscriptionSuccessful: '您的訂閱已成功設定。',
      downgradeNotice: '您將保留目前的訂閱及其功能直到本計費週期結束，然後您將被切換到此訂閱。',
      emailForm: {
        subtitle: '在您可以完成購買之前，您必須新增一個電子郵件地址，以便發送收據。',
        title: '新增電子郵件地址',
      },
      lineItems: {
        title__freeTrialEndsAt: '試用期將於',
        title__paymentMethod: '付款方式',
        title__statementId: '帳單 ID',
        title__subscriptionBegins: '訂閱開始',
        title__totalPaid: '總支付',
      },
      pastDueNotice: '您的上一個訂閱已逾期，未付款。',
      perMonth: '每月',
      title: '結帳',
      title__paymentSuccessful: '付款成功！',
      title__subscriptionSuccessful: '成功！',
      title__trialSuccess: '試用已成功開始！',
      totalDueAfterTrial: '試用期結束後 {{days}} 天內需要支付的金額',
      totalDuePerPeriod: undefined,
    },
    credit: '餘額',
    creditRemainder: '您目前訂閱的剩餘期間的餘額。',
    defaultFreePlanActive: '您目前正在免費計劃中',
    free: '免費',
    getStarted: '開始',
    highlightedPlanBadge: '熱門',
    keepFreeTrial: '保留免費試用',
    keepSubscription: '保留訂閱',
    manage: '管理',
    manageSubscription: '管理訂閱',
    month: '月',
    monthAbbreviation: undefined,
    monthPerUnit: undefined,
    monthly: '每月',
    pastDue: '逾期',
    pay: '支付 {{amount}}',
    payerCreditRemainder: '來自帳戶餘額的折抵。',
    paymentMethod: {
      applePayDescription: {
        annual: '每年',
        monthly: '每月',
      },
      dev: {
        anyNumbers: '任何數字',
        cardNumber: '卡號',
        cvcZip: 'CVC, ZIP',
        developmentMode: '開發模式',
        expirationDate: '到期日期',
        testCardInfo: '測試卡資訊',
      },
    },
    paymentMethods__label: '付款方式',
    pricingTable: {
      billingCycle: '計費週期',
      included: '包含',
      seatCost: {
        additionalSeats: undefined,
        freeUpToSeats: undefined,
        includedSeats: undefined,
        perSeat: undefined,
        tooltip: {
          additionalSeatsEach: undefined,
          firstSeatsIncludedInPlan: undefined,
          freeForUpToSeats: undefined,
        },
        unlimitedSeats: undefined,
        upToSeats: undefined,
      },
    },
    proratedDiscount: undefined,
    prorationCredit: '按比例折抵',
    reSubscribe: '重新訂閱',
    seatBreakdownIncludedPlural: undefined,
    seatBreakdownIncludedSingular: undefined,
    seatBreakdownPlural: undefined,
    seatBreakdownSingular: undefined,
    seats: undefined,
    seatsWithLimit: undefined,
    seeAllFeatures: '查看所有功能',
    startFreeTrial: '開始免費試用',
    startFreeTrial__days: '開始 {{days}} 天免費試用',
    subscribe: '訂閱',
    subscriptionDetails: {
      beginsOn: '開始於',
      currentBillingCycle: '當前計費週期',
      endsOn: '結束於',
      firstPaymentAmount: '第一次付款金額',
      firstPaymentOn: '第一次付款日期',
      nextPaymentAmount: '下次付款金額',
      nextPaymentOn: '下次付款日期',
      pastDueAt: '逾期日期',
      renewsAt: '續期日期',
      subscribedOn: '訂閱日期',
      title: '訂閱',
      trialEndsOn: '試用期結束日期',
      trialStartedOn: '試用期開始日期',
    },
    subtotal: '小計',
    subtotalRenewal: undefined,
    switchPlan: '切換到此方案',
    switchToAnnual: '切換到每年',
    switchToAnnualWithAnnualPrice: '切換到每年 {{price}} / 年',
    switchToMonthly: '切換到每月',
    switchToMonthlyWithPrice: '切換到每月 {{price}} / 月',
    totalDue: '總逾期金額',
    totalDuePerPeriod: undefined,
    totalDueToday: '總逾期金額',
    viewFeatures: '查看功能',
    viewPayment: '查看付款',
    year: '年',
    yearAbbreviation: undefined,
    yearPerUnit: undefined,
  },
  configureSSO: {
    activate: {
      activateButton: undefined,
      activeSubtitle: undefined,
      activeTitle: undefined,
      doneButton: undefined,
      skipButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    changeProviderDialog: {
      cancelButton: undefined,
      confirmButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    configureStep: {
      activeConnectionWarning: {
        dismiss: undefined,
        title: undefined,
      },
      attributeMappingTable: {
        badges: {
          optional: undefined,
          required: undefined,
        },
      },
      samlCustom: {
        assignUsersStep: {
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attributeName: undefined,
              userAttribute: undefined,
            },
            rows: {
              email: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              firstName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              lastName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            paragraph: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
      samlGoogle: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              appAttribute: undefined,
              googleAttribute: undefined,
            },
            rows: {
              email: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              firstName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              lastName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        configureUserAccess: {
          assignUsersInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          headerSubtitle: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataFile: {
            description: undefined,
            fileUploaded: undefined,
            label: undefined,
            removeFile: undefined,
            replaceFile: undefined,
            uploadFile: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataFile: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          nameIdInstructions: {
            step1: undefined,
            step2: undefined,
          },
          paragraph: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          title: undefined,
        },
      },
      samlMicrosoft: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attribute: undefined,
              claimName: undefined,
              value: undefined,
            },
            copyClaimName: undefined,
            copyClaimNameCopied: undefined,
            rows: {
              email: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              firstName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              lastName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          step1: undefined,
          step2: undefined,
          title: undefined,
        },
        createAppStep: {
          assignUsersInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: {
              label: undefined,
              subSteps: {
                appName: undefined,
                create: undefined,
                nonGallery: undefined,
              },
            },
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          step1: undefined,
          step2: undefined,
          step3: undefined,
          step4: undefined,
          step5: undefined,
          step6: undefined,
          title: undefined,
        },
      },
      samlOkta: {
        assignUsersStep: {
          assignUsersInstructions: {
            paragraph: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
          },
          headerSubtitle: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              expression: undefined,
              name: undefined,
            },
            rows: {
              email: {
                expression: undefined,
                name: undefined,
              },
              firstName: {
                expression: undefined,
                name: undefined,
              },
              lastName: {
                expression: undefined,
                name: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        createAppStep: {
          completeSamlIntegrationInstructions: {
            step1: undefined,
            step2: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            serviceProviderFields: {
              acsUrl: {
                label: undefined,
              },
              spEntityId: {
                label: undefined,
              },
            },
            title: undefined,
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
    },
    missingManageEnterpriseConnectionsPermission: {
      subtitle: '請聯絡您的組織管理員以升級您的權限。',
      title: '您沒有管理單一登入 (SSO) 的權限',
    },
    navbar: {
      title: '設定單一登入 (SSO)',
    },
    organizationDomainsStep: {
      domainCard: {
        badge__expired: undefined,
        badge__unverified: '未驗證',
        badge__verified: '已驗證',
        expiredAtLabel: undefined,
        expiredLabel: undefined,
        removeButtonTooltip__lastVerifiedDomain: undefined,
        removeButtonTooltip__lastVerifiedDomainActive: undefined,
        txtRecord: {
          hostLabel: '主機 / 名稱',
          instructions: '請將此 TXT 記錄新增至您的 DNS 供應商。記錄生效後，我們將自動進行驗證。',
          typeLabel: '類型',
          valueLabel: '值',
        },
        verifiedAtLabel: "驗證於 {{ date | shortDate('zh-TW') }}",
        verifyAgainButton: undefined,
      },
      domainSuggestion: {
        formButtonPrimary__add: '新增 {{domain}}',
        messageLabel: '您的電子郵件使用了 {{domain}}。是否要新增它？',
      },
      formButtonPrimary__add: '新增',
      formFieldInputPlaceholder__domain: '新增網域',
      formFieldLabel__domain: '網域',
      removeDomainDialog: {
        cancelButton: undefined,
        removeButton: undefined,
        subtitle__active: undefined,
        subtitle__inactive: undefined,
        title: undefined,
      },
      subtitle: '新增並驗證貴組織用於登入的網域的所有權。',
      title: '新增 SSO 網域',
    },
    resetConnectionDialog: {
      cancelButton: undefined,
      confirmationFieldLabel: undefined,
      confirmationFieldPlaceholder: undefined,
      resetButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    selectProviderStep: {
      saml: {
        customSaml: '自訂 SAML 提供者',
        google: undefined,
        groupLabel: 'SAML',
        microsoft: undefined,
        okta: 'Okta Workforce',
      },
      subtitle: '選擇您要為其設定 SSO 的提供者。',
      title: '選擇提供者',
      warning: '選擇提供者後,在設定完成之前無法再次變更',
    },
    testConfigurationStep: {
      error__noSuccessfulTestRun: undefined,
      subtitle: undefined,
      testResults: {
        actionLabel__refresh: undefined,
        empty: {
          subtitle: undefined,
          title: undefined,
        },
        polling: undefined,
        status__failed: undefined,
        status__pending: undefined,
        status__success: undefined,
        title: undefined,
      },
      testRunDetails: {
        howToFix: {
          actionLabel__viewDocumentation: undefined,
          oauth_access_denied: {
            description: undefined,
          },
          oauth_fetch_user_error: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
          },
          oauth_token_exchange_error: {
            description: undefined,
          },
          saml_email_address_domain_mismatch: {
            description: undefined,
          },
          saml_response_relaystate_missing: {
            description: undefined,
          },
          saml_user_attribute_missing: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          sectionTitle: undefined,
        },
        parsedUserInfo: {
          email: undefined,
          firstName: undefined,
          sectionTitle: undefined,
        },
        runDetails: {
          actionLabel__copied: undefined,
          actionLabel__copy: undefined,
          errorCode: undefined,
          fullMessage: undefined,
          sectionTitle: undefined,
          status: undefined,
          timestamp: undefined,
        },
        title: undefined,
      },
      testUrl: {
        actionLabel__open: undefined,
      },
      title: undefined,
    },
  },
  createOrganization: {
    formButtonSubmit: '創建組織',
    invitePage: {
      formButtonReset: '跳過',
    },
    title: '創建組織',
  },
  dates: {
    lastDay: "昨天{{ date | timeString('zh-TW') }}",
    next6Days: "{{ date | weekday('zh-TW','long') }} {{ date | timeString('zh-TW') }}",
    nextDay: "明天{{ date | timeString('zh-TW') }}",
    numeric: "{{ date | numeric('zh-TW') }}",
    previous6Days: "上週{{ date | weekday('zh-TW','long') }} {{ date | timeString('zh-TW') }}",
    sameDay: "今天{{ date | timeString('zh-TW') }}",
  },
  dividerText: '或者',
  footerActionLink__alternativePhoneCodeProvider: '改以簡訊傳送驗證碼',
  footerActionLink__useAnotherMethod: '使用另一種方法',
  footerPageLink__help: '幫助',
  footerPageLink__privacy: '隱私',
  footerPageLink__terms: '條款',
  formButtonPrimary: '繼續',
  formButtonPrimary__verify: '驗證',
  formFieldAction__forgotPassword: '忘記密碼？',
  formFieldError__matchingPasswords: '密碼匹配。',
  formFieldError__notMatchingPasswords: '密碼不匹配。',
  formFieldError__verificationLinkExpired: '驗證連結已過期，請請求新的連結。',
  formFieldHintText__optional: '選填',
  formFieldHintText__slug: 'slug 是一個易於人類閱讀且必須唯一的 ID，通常用於 URL。',
  formFieldInputPlaceholder__apiKeyDescription: '輸入此金鑰的用途',
  formFieldInputPlaceholder__apiKeyExpirationDate: '選擇日期',
  formFieldInputPlaceholder__apiKeyName: '輸入您的秘密金鑰名稱',
  formFieldInputPlaceholder__backupCode: '輸入備用碼',
  formFieldInputPlaceholder__confirmDeletionUserAccount: '刪除帳戶',
  formFieldInputPlaceholder__emailAddress: '輸入您的電子郵件地址',
  formFieldInputPlaceholder__emailAddress_username: '輸入電子郵件地址或使用者名稱',
  formFieldInputPlaceholder__emailAddresses: '輸入或貼上一個或多個電子郵件地址，以空格或逗號分隔',
  formFieldInputPlaceholder__firstName: '名字',
  formFieldInputPlaceholder__lastName: '姓氏',
  formFieldInputPlaceholder__organizationDomain: undefined,
  formFieldInputPlaceholder__organizationDomainEmailAddress: undefined,
  formFieldInputPlaceholder__organizationName: '組織名稱',
  formFieldInputPlaceholder__organizationSlug: '組織簡稱',
  formFieldInputPlaceholder__password: '輸入您的密碼',
  formFieldInputPlaceholder__phoneNumber: '輸入您的電話號碼',
  formFieldInputPlaceholder__signUpPassword: undefined,
  formFieldInputPlaceholder__username: undefined,
  formFieldInput__emailAddress_format: '範例格式：name@example.com',
  formFieldLabel__apiKey: 'API 金鑰',
  formFieldLabel__apiKeyDescription: '描述',
  formFieldLabel__apiKeyExpiration: '過期時間',
  formFieldLabel__apiKeyName: '金鑰名稱',
  formFieldLabel__automaticInvitations: '為此網域啟用自動邀請',
  formFieldLabel__backupCode: '備用碼',
  formFieldLabel__confirmDeletion: '確定',
  formFieldLabel__confirmPassword: '確認密碼',
  formFieldLabel__currentPassword: '目前密碼',
  formFieldLabel__emailAddress: '電子郵件地址',
  formFieldLabel__emailAddress_username: '電子郵件地址或使用者名稱',
  formFieldLabel__emailAddresses: '電子郵件地址',
  formFieldLabel__firstName: '名字',
  formFieldLabel__lastName: '姓氏',
  formFieldLabel__newPassword: '新密碼',
  formFieldLabel__organizationDomain: '網域',
  formFieldLabel__organizationDomainDeletePending: '刪除待處理的邀請和建議',
  formFieldLabel__organizationDomainEmailAddress: '驗證用電子郵件地址',
  formFieldLabel__organizationDomainEmailAddressDescription: '輸入此網域下的一個電子郵件地址以接收代碼並驗證此網域。',
  formFieldLabel__organizationName: '組織名稱',
  formFieldLabel__organizationSlug: 'URL 代稱',
  formFieldLabel__passkeyName: '金鑰名稱',
  formFieldLabel__password: '密碼',
  formFieldLabel__phoneNumber: '電話號碼',
  formFieldLabel__role: '角色',
  formFieldLabel__signOutOfOtherSessions: '登出所有其他裝置',
  formFieldLabel__username: '使用者名稱',
  identityPreviewEditButton__emailAddress: undefined,
  identityPreviewEditButton__identifier: undefined,
  identityPreviewEditButton__phoneNumber: undefined,
  impersonationFab: {
    action__signOut: '登出',
    title: '以 {{identifier}} 身份登入',
  },
  lastAuthenticationStrategy: '上次使用',
  maintenanceMode: '我們目前正在進行維護，但請放心此維護將不會超過幾分鐘。',
  membershipRole__admin: '管理員',
  membershipRole__basicMember: '成員',
  membershipRole__guestMember: '訪客',
  oauthConsent: {
    action__allow: undefined,
    action__deny: undefined,
    offlineAccessNotice: undefined,
    redirectNotice: undefined,
    redirectUriModal: {
      subtitle: undefined,
      title: undefined,
    },
    scopeList: {
      title: undefined,
    },
    subtitle: undefined,
    viewFullUrl: undefined,
    warning: undefined,
  },
  organizationList: {
    action__createOrganization: '建立組織',
    action__invitationAccept: '加入',
    action__suggestionsAccept: '請求加入',
    createOrganization: '建立組織',
    invitationAcceptedLabel: '已加入',
    subtitle: '以繼續前往 {{applicationName}}',
    suggestionsAcceptedLabel: '待核准',
    title: '選擇一個帳戶',
    titleWithoutPersonal: '選擇一個組織',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'API 金鑰',
    },
    badge__automaticInvitation: '自動邀請',
    badge__automaticSuggestion: '自動建議',
    badge__enterpriseSso: undefined,
    badge__manualInvitation: '未啟用自動註冊',
    badge__unverified: '未驗證',
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: '沒有付款歷史',
        notFound: '找不到這筆付款紀錄',
        tableHeader__amount: '金額',
        tableHeader__date: '日期',
        tableHeader__status: '狀態',
      },
      paymentMethodsSection: {
        actionLabel__default: '設為預設',
        actionLabel__remove: '移除',
        add: '新增付款方式',
        addSubtitle: '新增付款方式到您的帳戶。',
        cancelButton: '取消',
        formButtonPrimary__add: '新增付款方式',
        formButtonPrimary__pay: '支付 {{amount}}',
        payWithTestCardButton: '使用測試卡支付',
        removeMethod: {
          messageLine1: '{{identifier}} 將從此帳戶中移除。',
          messageLine2: '您將不再能夠使用此付款方式，並且依賴它的任何定期訂閱將不再有效。',
          successMessage: '{{paymentMethod}} 已從您的帳戶中移除。',
          title: '移除付款方式',
        },
        title: '付款方式',
      },
      start: {
        headerTitle__payments: '付款',
        headerTitle__plans: '方案',
        headerTitle__statements: '帳單',
        headerTitle__subscriptions: '訂閱',
      },
      statementsSection: {
        empty: '沒有帳單要顯示',
        itemCaption__paidForPlan: '支付 {{plan}} {{period}} 方案',
        itemCaption__payerCredit: '來自帳戶餘額的折抵',
        itemCaption__proratedCredit: '部分使用前一個訂閱的剩餘期間的餘額',
        itemCaption__subscribedAndPaidForPlan: '訂閱並支付 {{plan}} {{period}} 方案',
        notFound: '找不到這筆帳單',
        tableHeader__amount: '金額',
        tableHeader__date: '日期',
        title: '帳單',
        totalPaid: '總支付',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: '管理',
        actionLabel__newSubscription: '訂閱一個方案',
        actionLabel__switchPlan: '切換方案',
        includedSeatsUsage: undefined,
        overview: undefined,
        paidSeatsUsage: undefined,
        seatLimit: undefined,
        seatLimitAndIncludedSeats: undefined,
        tableHeader__edit: '編輯',
        tableHeader__plan: '方案',
        tableHeader__startDate: '開始日期',
        title: '訂閱',
      },
      subscriptionsSection: {
        actionLabel__default: '管理',
      },
      switchPlansSection: {
        title: '切換方案',
      },
      title: '結帳',
    },
    createDomainPage: {
      subtitle: '新增要驗證的網域。在此網域下擁有電子郵件地址的使用者可以自動加入組織或請求加入。',
      title: '新增網域',
    },
    invitePage: {
      detailsTitle__inviteFailed: '邀請無法傳送。請修正以下問題後再試一次：',
      formButtonPrimary__continue: '傳送邀請',
      formButtonPrimary__purchaseSeats: undefined,
      selectDropdown__role: '選取角色',
      subtitle: '邀請新成員加入此組織',
      successMessage: '邀請已成功傳送',
      title: '邀請成員',
    },
    membersPage: {
      action__invite: '邀請',
      action__search: '搜尋',
      activeMembersTab: {
        menuAction__remove: '移除成員',
        tableHeader__actions: '操作',
        tableHeader__joined: '加入',
        tableHeader__role: '角色',
        tableHeader__user: '使用者',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: '我們正在更新可用角色。完成後，您將能夠再次更新角色。',
          title: '角色暫時被鎖定',
        },
      },
      detailsTitle__emptyRow: '沒有可顯示的成員',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            '透過將電子郵件網域與您的組織連結來邀請使用者。任何使用匹配的電子郵件網域註冊的人都可以隨時加入該組織。',
          headerTitle: '自動邀請',
          primaryButton: '管理已驗證網域',
        },
        table__emptyRow: '沒有可顯示的邀請',
      },
      invitedMembersTab: {
        menuAction__revoke: '撤銷邀請',
        tableHeader__invited: '已邀請',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle: '使用匹配的電子郵件網域註冊的使用者將能夠看到請求加入您組織的建議。',
          headerTitle: '自動建議',
          primaryButton: '管理已驗證網域',
        },
        menuAction__approve: '核准',
        menuAction__reject: '拒絕',
        tableHeader__requested: '請求存取',
        table__emptyRow: '沒有可顯示的請求',
      },
      start: {
        headerTitle__invitations: '邀請',
        headerTitle__members: '成員',
        headerTitle__requests: '請求',
      },
    },
    navbar: {
      apiKeys: 'API 金鑰',
      billing: '結帳',
      description: '管理您的組織設定。',
      general: '一般',
      members: '成員',
      security: undefined,
      title: '組織',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: '您沒有管理此組織結帳的權限。',
        planMembershipLimitExceeded: undefined,
      },
      title: '方案',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: '在下方輸入「{{organizationName}}」以繼續。',
          messageLine1: '您確定要刪除此組織嗎？',
          messageLine2: '此動作將永久生效且無法復原。',
          successMessage: '您已刪除該組織。',
          title: '刪除組織',
        },
        leaveOrganization: {
          actionDescription: '在下方輸入「{{organizationName}}」以繼續。',
          messageLine1: '您確定要離開此組織嗎？您將失去對此組織及其應用程式的存取權限。',
          messageLine2: '此動作將永久生效且無法復原。',
          successMessage: '您已離開該組織。',
          title: '離開組織',
        },
        title: '危險',
      },
      domainSection: {
        menuAction__manage: '管理',
        menuAction__remove: '刪除',
        menuAction__verify: '驗證',
        primaryButton: '新增網域',
        subtitle: '允許使用者根據已驗證的電子郵件網域自動加入組織或請求加入。',
        title: '已驗證網域',
      },
      successMessage: '組織資訊已更新。',
      title: '組織設定',
    },
    removeDomainPage: {
      messageLine1: '電子郵件網域 {{domain}} 將被移除。',
      messageLine2: '此後，使用者將無法透過此網域自動加入組織。',
      successMessage: '網域 {{domain}} 已被移除。',
      title: '移除網域',
    },
    securityPage: {
      removeDialog: {
        confirmButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
      ssoSection: {
        badge__active: undefined,
        badge__inProgress: undefined,
        badge__inactive: undefined,
        badge__unconfigured: undefined,
        descriptionLine1: undefined,
        domainLabel: undefined,
        menuAction__activate: undefined,
        menuAction__deactivate: undefined,
        menuAction__edit: undefined,
        menuAction__remove: undefined,
        primaryButton__continueConfiguration: undefined,
        primaryButton__startConfiguration: undefined,
        title: undefined,
        tooltip: undefined,
        tooltipLabel: undefined,
        tooltip__noRole: undefined,
      },
      title: undefined,
    },
    start: {
      headerTitle__general: '一般',
      headerTitle__members: '成員',
      membershipSeatUsageLabel: undefined,
      profileSection: {
        primaryButton: '更新組織資料',
        title: '組織設定',
        uploadAction__title: '標誌',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: '移除此網域將影響已邀請的使用者。',
        removeDomainActionLabel__remove: '移除網域',
        removeDomainSubtitle: '從您已驗證的網域中移除此網域',
        removeDomainTitle: '移除網域',
      },
      enrollmentTab: {
        automaticInvitationOption__description: '使用者註冊時會自動受邀加入組織，並可隨時加入。',
        automaticInvitationOption__label: '自動邀請',
        automaticSuggestionOption__description: '使用者會收到請求加入的建議，但必須由管理員核准後才能加入組織。',
        automaticSuggestionOption__label: '自動建議',
        calloutInfoLabel: '變更註冊模式只會影響新使用者。',
        calloutInvitationCountLabel: '已傳送給使用者的待處理邀請：{{count}}',
        calloutSuggestionCountLabel: '已傳送給使用者的待處理建議：{{count}}',
        manualInvitationOption__description: '使用者只能透過手動邀請加入組織。',
        manualInvitationOption__label: '未啟用自動註冊',
        subtitle: '選擇此網域的使用者如何加入組織。',
      },
      start: {
        headerTitle__danger: '危險',
        headerTitle__enrollment: '註冊選項',
      },
      subtitle: '網域 {{domain}} 目前已驗證。請繼續選擇註冊模式。',
      title: '更新 {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: '請輸入傳送至您電子郵件地址的驗證碼',
      formTitle: '驗證碼',
      resendButton: '沒收到代碼？重新傳送',
      subtitle: '網域 {{domainName}} 需要透過電子郵件進行驗證。',
      subtitleVerificationCodeScreen: '驗證碼已傳送至 {{emailAddress}}。請輸入代碼以繼續。',
      title: '驗證網域',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: '關閉組織切換選單',
    action__createOrganization: '建立組織',
    action__invitationAccept: '加入',
    action__manageOrganization: '管理組織',
    action__openOrganizationSwitcher: '開啟組織切換選單',
    action__suggestionsAccept: '請求加入',
    notSelected: '未選取組織',
    personalWorkspace: '個人工作區',
    suggestionsAcceptedLabel: '待核准',
  },
  paginationButton__next: '下一頁',
  paginationButton__previous: '上一頁',
  paginationRowText__displaying: '顯示',
  paginationRowText__of: '的',
  reverification: {
    alternativeMethods: {
      actionLink: '取得協助',
      actionText: '沒有以上任何一種方式嗎？',
      blockButton__backupCode: '使用備用碼',
      blockButton__emailCode: '傳送驗證碼至 {{identifier}}',
      blockButton__passkey: '使用您的金鑰',
      blockButton__password: '使用您的密碼登入',
      blockButton__phoneCode: '傳送簡訊代碼至 {{identifier}}',
      blockButton__totp: '使用您的驗證器應用程式',
      getHelp: {
        blockButton__emailSupport: '電子郵件支援',
        content: '如果您登入帳戶時遇到問題，請傳送電子郵件給我們，我們將盡快協助您恢復存取權限。',
        title: '取得協助',
      },
      subtitle: '遇到問題？您可以使用下列任一方法登入。',
      title: '使用其他方式',
    },
    backupCodeMfa: {
      subtitle: '請輸入您在設定兩步驟驗證時收到的驗證碼以繼續',
      title: '輸入驗證碼',
    },
    emailCode: {
      formTitle: '驗證碼',
      resendButton: '沒收到代碼？重新傳送',
      subtitle: '請輸入已寄至您電子郵件的驗證碼以繼續',
      title: '需要驗證您的身分',
    },
    noAvailableMethods: {
      message: '無法繼續驗證。沒有可用的驗證方式。',
      subtitle: '發生錯誤',
      title: '無法驗證您的帳戶',
    },
    passkey: {
      blockButton__passkey: '使用您的金鑰',
      subtitle: '使用您的金鑰確認您的身分。您的裝置可能會要求您的指紋、臉部或螢幕鎖。',
      title: '使用您的金鑰',
    },
    password: {
      actionLink: '使用其他方式',
      subtitle: '請輸入您的密碼以繼續',
      title: '需要驗證您的身分',
    },
    phoneCode: {
      formTitle: '驗證碼',
      resendButton: '沒收到代碼？重新傳送',
      subtitle: '請輸入已寄至您手機的驗證碼以繼續',
      title: '需要驗證您的身分',
    },
    phoneCodeMfa: {
      formTitle: '驗證碼',
      resendButton: '沒收到代碼？重新傳送',
      subtitle: '請輸入已寄至您手機的驗證碼以繼續',
      title: '需要驗證您的身分',
    },
    totpMfa: {
      formTitle: '驗證碼',
      subtitle: '請輸入您在設定兩步驟驗證時收到的驗證碼以繼續',
      title: '輸入驗證碼',
    },
  },
  searchInput: {
    action__clear: undefined,
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: '新增帳戶',
      action__signOutAll: '登出所有帳戶',
      subtitle: '選擇您想要繼續使用的帳戶。',
      title: '選擇一個帳戶',
    },
    alternativeMethods: {
      actionLink: '取得協助',
      actionText: '沒有以上任何一種方式嗎？',
      blockButton__backupCode: '使用備用碼',
      blockButton__emailCode: '傳送驗證碼至 {{identifier}}',
      blockButton__emailLink: '傳送電子郵件連結至 {{identifier}}',
      blockButton__passkey: '使用您的金鑰',
      blockButton__password: '使用您的密碼登入',
      blockButton__phoneCode: '傳送簡訊代碼至 {{identifier}}',
      blockButton__totp: '使用您的驗證器應用程式',
      getHelp: {
        blockButton__emailSupport: '電子郵件支援',
        content: '如果您登入帳戶時遇到問題，請傳送電子郵件給我們，我們將盡快協助您恢復存取權限。',
        title: '取得協助',
      },
      subtitle: '遇到問題？您可以使用下列任一方法登入。',
      title: '使用其他方式',
    },
    alternativePhoneCodeProvider: {
      formTitle: '驗證碼',
      resendButton: '沒收到代碼？重新傳送',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '檢查您的 {{provider}}',
    },
    backupCodeMfa: {
      subtitle: '以繼續前往 {{applicationName}}',
      title: '輸入備用碼',
    },
    emailCode: {
      formTitle: '驗證碼',
      resendButton: '重新傳送驗證碼',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '檢查您的電子郵件',
    },
    emailCodeMfa: {
      formTitle: '檢查您的電子郵件',
      resendButton: '沒收到驗證碼？重新傳送',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '檢查您的電子郵件',
    },
    emailLink: {
      clientMismatch: {
        subtitle: '請回到原本登入時使用的裝置與瀏覽器，點開驗證連結以繼續登入',
        title: '驗證連結無效於此裝置',
      },
      expired: {
        subtitle: '返回原先的分頁以繼續。',
        title: '此驗證連結已過期',
      },
      failed: {
        subtitle: '返回原先的分頁以繼續。',
        title: '此驗證連結無效',
      },
      formSubtitle: '使用傳送至您電子郵件的驗證連結',
      formTitle: '驗證連結',
      loading: {
        subtitle: '即將為您重新導向',
        title: '正在登入…',
      },
      resendButton: '重新傳送連結',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '檢查您的電子郵件',
      unusedTab: {
        title: '您可以關閉此分頁',
      },
      verified: {
        subtitle: '即將為您重新導向',
        title: '成功登入',
      },
      verifiedSwitchTab: {
        subtitle: '返回原先的分頁以繼續',
        subtitleNewTab: '返回新開啟的分頁以繼續',
        titleNewTab: '已在其他分頁登入',
      },
    },
    emailLinkMfa: {
      formSubtitle: '使用發送到您電子郵件的驗證連結',
      resendButton: '沒有收到連結？重新發送',
      subtitle: '繼續使用 {{applicationName}}',
      title: '查看您的電子郵件',
    },
    enterpriseConnections: {
      subtitle: '請選擇您想要繼續使用的企業帳戶。',
      title: '選擇一個企業帳戶',
    },
    forgotPassword: {
      formTitle: '重設密碼代碼',
      resendButton: '重新傳送代碼',
      subtitle: '以重設您的密碼',
      subtitle_email: '首先，請輸入傳送至您電子郵件的代碼',
      subtitle_phone: '首先，請輸入傳送至您手機的代碼',
      title: '重設密碼',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: '重設密碼',
      label__alternativeMethods: '或者，使用其他方式登入。',
      title: '忘記密碼？',
    },
    newDeviceVerificationNotice: '您正在從新裝置登入。我們需要驗證以保護您的帳戶安全。',
    noAvailableMethods: {
      message: '無法繼續登入。沒有可用的驗證方式。',
      subtitle: '發生錯誤',
      title: '無法登入',
    },
    passkey: {
      subtitle: '使用您的金鑰確認您的身分。您的裝置可能會要求您的指紋、臉部或螢幕鎖。',
      title: '使用您的金鑰',
    },
    password: {
      actionLink: '使用其他方式',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '輸入您的密碼',
    },
    passwordCompromised: {
      title: '密碼可能已遭外洩',
    },
    passwordPwned: {
      title: '密碼可能已遭外洩',
    },
    passwordUntrusted: {
      title: '密碼不受信任',
    },
    phoneCode: {
      formTitle: '驗證碼',
      resendButton: '重新傳送驗證碼',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '檢查手機簡訊',
    },
    phoneCodeMfa: {
      formTitle: '驗證碼',
      resendButton: '重新傳送驗證碼',
      subtitle: '請輸入已寄至您手機的驗證碼以繼續',
      title: '檢查手機簡訊',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    resetPassword: {
      formButtonPrimary: '重設密碼',
      requiredMessage: '基於安全考量，您必須重設密碼。',
      successMessage: '您的密碼已成功變更。正在為您登入，請稍候。',
      title: '重設密碼',
    },
    resetPasswordMfa: {
      detailsLabel: '我們需要驗證您的身份才能重設您的密碼。',
    },
    start: {
      actionLink: '註冊',
      actionLink__join_waitlist: '加入等候名單',
      actionLink__use_email: '使用電子郵件',
      actionLink__use_email_username: '使用電子郵件或使用者名稱',
      actionLink__use_passkey: '使用金鑰',
      actionLink__use_phone: '使用電話號碼',
      actionLink__use_username: '使用使用者名稱',
      actionText: '還沒有帳戶嗎？',
      actionText__join_waitlist: '想要早期存取？',
      alternativePhoneCodeProvider: {
        actionLink: '使用其他方式',
        label: '{{provider}} 電話號碼',
        subtitle: '請輸入您的電話號碼以取得 {{provider}} 的驗證碼。',
        title: '使用 {{provider}} 登入 {{applicationName}}',
      },
      subtitle: '以繼續前往 {{applicationName}}',
      subtitleCombined: undefined,
      title: '登入',
      titleCombined: '繼續前往 {{applicationName}}',
    },
    totpMfa: {
      formTitle: '驗證碼',
      subtitle: '請輸入您在設定兩步驟驗證時收到的驗證碼以繼續',
      title: '兩步驟驗證',
    },
    web3Solana: {
      subtitle: '請選擇下方錢包以登入',
      title: '使用 Solana 登入',
    },
  },
  signInEnterPasswordTitle: '輸入您的密碼',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: '沒收到代碼？重新傳送',
      subtitle: '請輸入已寄至您 {{provider}} 的驗證碼以繼續',
      title: '檢查您的 {{provider}}',
    },
    continue: {
      actionLink: '登入',
      actionText: '已經有帳戶了嗎？',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '填寫缺少的欄位',
    },
    emailCode: {
      formSubtitle: '請輸入傳送至您電子郵件地址的驗證碼',
      formTitle: '驗證碼',
      resendButton: '重新傳送驗證碼',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '驗證您的電子郵件',
    },
    emailLink: {
      clientMismatch: {
        subtitle: '請回到原本登入時使用的裝置與瀏覽器，點開驗證連結以繼續登入',
        title: '驗證連結無效於此裝置',
      },
      formSubtitle: '使用傳送至您電子郵件地址的驗證連結',
      formTitle: '驗證連結',
      loading: {
        title: '正在註冊…',
      },
      resendButton: '重新傳送連結',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '驗證您的電子郵件',
      verified: {
        title: '註冊成功',
      },
      verifiedSwitchTab: {
        subtitle: '返回新開啟的分頁以繼續',
        subtitleNewTab: '返回上一個分頁以繼續',
        title: '電子郵件驗證成功',
      },
    },
    enterpriseConnections: {
      subtitle: '請選擇您想要繼續使用的企業帳戶。',
      title: '選擇一個企業帳戶',
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: '我同意 {{ privacyPolicyLink || link("隱私權政策") }}',
        label__onlyTermsOfService: '我同意 {{ termsOfServiceLink || link("服務條款") }}',
        label__termsOfServiceAndPrivacyPolicy:
          '我同意 {{ termsOfServiceLink || link("服務條款") }} 與 {{ privacyPolicyLink || link("隱私權政策") }}',
      },
      continue: {
        subtitle: '請閱讀並接受條款以繼續',
        title: '同意條款',
      },
    },
    phoneCode: {
      formSubtitle: '輸入傳送到您的電話號碼的驗證碼',
      formTitle: '驗證碼',
      resendButton: '重新傳送驗證碼',
      subtitle: '以繼續前往 {{applicationName}}',
      title: '驗證您的電話號碼',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    restrictedAccess: {
      actionLink: '登入',
      actionText: '已經有帳戶了嗎？',
      blockButton__emailSupport: '電子郵件支援',
      blockButton__joinWaitlist: '加入等候名單',
      subtitle: '登入目前受到限制。如果您認為您應該有存取權，請聯繫支援。',
      subtitleWaitlist: '登入目前受到限制。想要早期存取？加入等候名單。',
      title: '存取受到限制',
    },
    start: {
      actionLink: '登入',
      actionLink__use_email: '使用電子郵件',
      actionLink__use_phone: '使用電話號碼',
      actionText: '已經有帳戶了嗎？',
      alternativePhoneCodeProvider: {
        actionLink: '使用其他方式',
        label: '{{provider}} 電話號碼',
        subtitle: '請輸入您的電話號碼以取得 {{provider}} 的驗證碼。',
        title: '使用 {{provider}} 登入 {{applicationName}}',
      },
      subtitle: '以繼續前往 {{applicationName}}',
      subtitleCombined: '以繼續前往 {{applicationName}}',
      title: '建立您的帳戶',
      titleCombined: '建立您的帳戶',
    },
    web3Solana: {
      subtitle: '請選擇下方錢包以註冊',
      title: '使用 Solana 註冊',
    },
  },
  socialButtonsBlockButton: '以 {{provider|titleize}} 帳戶登入',
  socialButtonsBlockButtonManyInView: undefined,
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        '偵測到的公司名稱 ({{organizationName}}) 和 {{organizationDomain}} 已存在一個組織。請透過邀請加入。',
    },
    chooseOrganization: {
      action__createOrganization: '建立新組織',
      action__invitationAccept: '加入',
      action__suggestionsAccept: '請求加入',
      subtitle: '加入現有組織或建立新組織',
      subtitle__createOrganizationDisabled: '加入現有組織',
      suggestionsAcceptedLabel: '等待批准',
      title: '選擇組織',
    },
    createOrganization: {
      formButtonReset: '取消',
      formButtonSubmit: '繼續',
      formFieldInputPlaceholder__name: '我的組織',
      formFieldInputPlaceholder__slug: 'wo-de-zu-zhi',
      formFieldLabel__name: '名稱',
      formFieldLabel__slug: 'Slug',
      subtitle: '輸入您的組織詳細資訊以繼續',
      title: '設定您的組織',
    },
    organizationCreationDisabled: {
      subtitle: '請聯繫您的組織管理員以獲取邀請。',
      title: '您必須屬於一個組織',
    },
    signOut: {
      actionLink: '登出',
      actionText: '已登入為 {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: '重設密碼',
    signOut: {
      actionLink: '登出',
      actionText: '已登入為 {{identifier}}',
    },
    subtitle: '您的帳戶需要新的密碼才能繼續',
    title: '重設您的密碼',
  },
  taskSetupMfa: {
    badge: '兩步驟驗證設定',
    signOut: {
      actionLink: '登出',
      actionText: '已登入為 {{identifier}}',
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: '繼續',
        infoText: '將會傳送驗證碼至您的電話號碼。',
      },
      addPhoneNumber: '新增電話號碼',
      cancel: '取消',
      subtitle: '選擇您想要使用於兩步驟驗證的電話號碼',
      success: {
        finishButton: '繼續',
        message1: '兩步驟驗證現已啟用。登入時，您需要輸入傳送至此電話號碼的驗證碼作為額外步驟。',
        message2: '請妥善保管這些備用碼，並將其存放在安全的地方。若您遺失驗證裝置，可以使用備用碼登入。',
        title: 'SMS 驗證碼已啟用',
      },
      title: '新增 SMS 驗證碼',
      verifyPhone: {
        formButtonPrimary: '繼續',
        formTitle: '驗證碼',
        resendButton: '沒收到驗證碼？重新傳送',
        subtitle: '請輸入傳送至',
        title: '驗證您的電話號碼',
      },
    },
    start: {
      methodSelection: {
        phoneCode: 'SMS 驗證碼',
        totp: '驗證器應用程式',
      },
      subtitle: '選擇您想要使用於兩步驟驗證的方法',
      title: '設定兩步驟驗證',
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: '掃描 QR 碼代替',
        buttonUnableToScan__nonPrimary: '無法掃描 QR 碼？',
        formButtonPrimary: '繼續',
        formButtonReset: '取消',
        infoText__ableToScan: '在驗證器應用程式中設定新的登入方法，並掃描以下 QR 碼以連結至您的帳戶。',
        infoText__unableToScan: '在驗證器中設定新的登入方法，並掃描以下 QR 碼以連結至您的帳戶。',
        inputLabel__unableToScan1: '請確認 Time-based 或 One-time passwords 已啟用，然後完成帳戶連結。',
      },
      success: {
        finishButton: '繼續',
        message1: '兩步驟驗證現已啟用。登入時，您需要輸入傳送至此驗證器應用程式的驗證碼作為額外步驟。',
        message2: '請妥善保管這些備用碼，並將其存放在安全的地方。若您遺失驗證裝置，可以使用備用碼登入。',
        title: '驗證器應用程式已啟用',
      },
      title: '新增驗證器應用程式',
      verifyTotp: {
        formButtonPrimary: '繼續',
        formButtonReset: '取消',
        formTitle: '驗證碼',
        subtitle: '請輸入驗證器應用程式生成的驗證碼',
        title: '新增驗證器應用程式',
      },
    },
  },
  unstable__errors: {
    action_blocked: undefined,
    already_a_member_in_organization: '{{email}} 已經是組織的成員。',
    api_key_name_already_exists: undefined,
    api_key_usage_exceeded: undefined,
    avatar_file_size_exceeded: '檔案大小超過10MB的上限。請選擇較小的檔案。',
    avatar_file_type_invalid: '不支援的檔案類型。請上傳 JPG、PNG、GIF 或 WEBP 格式的圖片。',
    captcha_invalid: '由於安全驗證失敗，註冊未成功。請重新整理頁面再試一次，或聯絡支援以取得協助。',
    captcha_unavailable: '由於機器人驗證失敗導致註冊未成功。請重新整理頁面再試一次，或聯絡支援以取得協助。',
    form_code_incorrect: undefined,
    form_email_address_blocked: undefined,
    form_identifier_exists__email_address: undefined,
    form_identifier_exists__phone_number: undefined,
    form_identifier_exists__username: undefined,
    form_identifier_not_found: '我們無法找到具有這些資訊的帳戶。',
    form_new_password_matches_current: '新密碼不能與目前的密碼相同。',
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: '電子郵件地址必須是有效的格式。',
    form_param_format_invalid__phone_number: '電話號碼必須是有效的國際格式。',
    form_param_max_length_exceeded__first_name: '名字不應超過 256 個字元。',
    form_param_max_length_exceeded__last_name: '姓氏不應超過 256 個字元。',
    form_param_max_length_exceeded__name: '名稱不應超過 256 個字元。',
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: '您的密碼太短。長度必須至少為 8 個字元。',
    form_password_not_strong_enough: '您的密碼強度不足。',
    form_password_or_identifier_incorrect: '密碼或電子郵件地址不正確。請重試或使用其他方法。',
    form_password_pwned: '此密碼已在已知的資料外洩事件中出現，請改用其他密碼。',
    form_password_pwned__sign_in: '此密碼曾出現在資料外洩事件中，無法使用，請重設密碼。',
    form_password_size_in_bytes_exceeded: '您的密碼超過允許的大小上限，請縮短或移除部分特殊字元。',
    form_password_untrusted__sign_in:
      '您的密碼可能已外洩。為保護您的帳戶，請改用其他登入方式。登入後您將需要重設密碼。',
    form_password_validation_failed: '密碼驗證失敗',
    form_username_invalid_character: undefined,
    form_username_invalid_length: '您的使用者名稱長度必須介於 {{min_length}} 與 {{max_length}} 個字元之間。',
    form_username_needs_non_number_char: '您的使用者名稱必須包含至少一個非數字字元。',
    identification_deletion_failed: '您無法刪除最後一個驗證方式。',
    insufficient_seats_change_plan: undefined,
    insufficient_seats_contact_support: undefined,
    not_allowed_access: undefined,
    oauth_access_denied: undefined,
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: undefined,
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized: '您已不再是組織的成員。請選擇或建立另一個組織。',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      '您已不再是組織的成員。請選擇另一個組織。',
    passkey_already_exists: '此裝置已註冊金鑰。',
    passkey_not_supported: '此裝置不支援金鑰。',
    passkey_pa_not_supported: '註冊需要平台驗證器，但此裝置不支援。',
    passkey_registration_cancelled: '金鑰註冊已取消或逾時。',
    passkey_retrieval_cancelled: '金鑰驗證已取消或逾時。',
    passwordComplexity: {
      maximumLength: '少於 {{length}} 個字元',
      minimumLength: '{{length}} 個或更多字元',
      requireLowercase: '一個小寫字母',
      requireNumbers: '一個數字',
      requireSpecialCharacter: '一個特殊字元',
      requireUppercase: '一個大寫字母',
      sentencePrefix: '您的密碼必須包含：',
    },
    phone_number_exists: '此電話號碼已被使用，請嘗試其他號碼。',
    protect_check_aborted: undefined,
    protect_check_already_resolved: undefined,
    protect_check_execution_failed: undefined,
    protect_check_invalid_script: undefined,
    protect_check_invalid_sdk_url: undefined,
    protect_check_script_load_failed: undefined,
    protect_check_timed_out: undefined,
    protect_check_unsupported_environment: undefined,
    session_exists: '您已經登錄。',
    web3_missing_identifier: '找不到 Web3 錢包擴充功能。請安裝以繼續。',
    web3_signature_request_rejected: '您已拒絕簽名請求。請再試一次以繼續。',
    web3_solana_signature_generation_failed: '產生簽名時發生錯誤。請再試一次以繼續。',
    zxcvbn: {
      couldBeStronger: '您的密碼強度尚可，但可以更安全。請嘗試增加長度或複雜度。',
      goodPassword: '密碼強度良好。',
      notEnough: '您的密碼強度不足。',
      suggestions: {
        allUppercase: '部分字母大寫，但非全部。',
        anotherWord: '增加更多、更少見的單字。',
        associatedYears: '避免使用與您相關的年份。',
        capitalization: '不僅首字母大寫。',
        dates: '避免使用與您相關的日期和年份。',
        l33t: '避免可預測的字母替換（例如用 "@" 取代 "a"）。',
        longerKeyboardPattern: '使用更長的鍵盤排列，並多次改變方向。',
        noNeed: '您無需使用符號、數字或大寫字母也能建立高強度密碼。',
        pwned: '若您在其他地方使用過此密碼，建議立即更換。',
        recentYears: '避免使用近年的年份。',
        repeated: '避免重複的單字和字元。',
        reverseWords: '避免常用詞語的反向拼寫。',
        sequences: '避免常見的字元序列。',
        useWords: '使用多個單字，但避免常用片語。',
      },
      warnings: {
        common: '這是一個常用的密碼。',
        commonNames: '常見的姓名容易被猜到。',
        dates: '日期易被猜到。',
        extendedRepeat: '重複的字元模式（例如 "abcabcabc"）容易被猜到。',
        keyPattern: '簡短的鍵盤排列容易被猜到。',
        namesByThemselves: '單獨使用姓名容易被猜到。',
        pwned: '您的密碼已在公開的資料外洩事件中出現。',
        recentYears: '近年的年份容易被猜到。',
        sequences: '常見的字元序列（例如 "abc"）容易被猜到。',
        similarToCommon: '此密碼與常見密碼相似。',
        simpleRepeat: '重複的字元（例如 "aaa"）容易被猜到。',
        straightRow: '鍵盤上成直線的按鍵容易被猜到。',
        topHundred: '這是非常常用的密碼。',
        topTen: '這是極常用的密碼。',
        userInputs: '密碼不應包含個人或頁面相關資訊。',
        wordByItself: '單獨使用單字容易被猜到。',
      },
    },
  },
  userButton: {
    action__addAccount: '新增帳戶',
    action__closeUserMenu: '關閉使用者選單',
    action__manageAccount: '管理帳戶',
    action__openUserMenu: '開啟使用者選單',
    action__signOut: '登出',
    action__signOutAll: '登出所有帳戶',
    label__accountActions: '帳戶操作',
    label__activeSessions: '活躍工作階段',
    label__userButtonPopover: '帳戶面板',
  },
  userProfile: {
    apiKeysPage: {
      title: 'API 金鑰',
    },
    backupCodePage: {
      actionLabel__copied: '已複製！',
      actionLabel__copy: '全部複製',
      actionLabel__download: '下載 .txt',
      actionLabel__print: '列印',
      infoText1: '將為此帳戶啟用備用碼。',
      infoText2: '請妥善保管您的備用碼。若懷疑已外洩，您可以重新產生。',
      subtitle__codelist: '請妥善保管。',
      successMessage: '備用碼現已啟用。若您無法存取驗證裝置，可使用其中一組備用碼登入。每組代碼僅能使用一次。',
      successSubtitle: '若您無法存取驗證裝置，可使用其中一組備用碼登入您的帳戶。',
      title: '新增備用碼驗證',
      title__codelist: '備用碼',
    },
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: '沒有付款紀錄',
        notFound: '找不到付款紀錄',
        tableHeader__amount: '金額',
        tableHeader__date: '日期',
        tableHeader__status: '狀態',
      },
      paymentMethodsSection: {
        actionLabel__default: '設為預設',
        actionLabel__remove: '移除',
        add: '新增付款方式',
        addSubtitle: '新增付款方式到您的帳戶。',
        cancelButton: '取消',
        formButtonPrimary__add: '新增付款方式',
        formButtonPrimary__pay: '支付 {{amount}}',
        payWithTestCardButton: '使用測試卡支付',
        removeMethod: {
          messageLine1: '{{identifier}} 將從此帳戶中被移除。',
          messageLine2: '您將不再能夠使用此付款方式，並且依賴它的任何定期訂閱將不再有效。',
          successMessage: '{{paymentMethod}} 已從您的帳戶中移除。',
          title: '移除付款方式',
        },
        title: '付款方式',
      },
      start: {
        headerTitle__payments: '付款',
        headerTitle__plans: '方案',
        headerTitle__statements: '帳單',
        headerTitle__subscriptions: '訂閱',
      },
      statementsSection: {
        empty: '沒有帳單可顯示',
        itemCaption__paidForPlan: '已支付 {{plan}} {{period}} 方案',
        itemCaption__payerCredit: '來自帳戶餘額的折抵',
        itemCaption__proratedCredit: '部分使用前一個訂閱的折抵金額',
        itemCaption__subscribedAndPaidForPlan: '已訂閱並支付 {{plan}} {{period}} 方案',
        notFound: '找不到帳單',
        tableHeader__amount: '金額',
        tableHeader__date: '日期',
        title: '帳單',
        totalPaid: '總支付金額',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: '管理',
        actionLabel__newSubscription: '訂閱一個方案',
        actionLabel__switchPlan: '切換方案',
        overview: undefined,
        tableHeader__edit: '編輯',
        tableHeader__plan: '方案',
        tableHeader__startDate: '開始日期',
        title: '訂閱',
      },
      subscriptionsSection: {
        actionLabel__default: '管理',
      },
      switchPlansSection: {
        title: '切換方案',
      },
      title: '結帳',
    },
    connectedAccountPage: {
      formHint: '選擇一個供應商以連結您的帳戶。',
      formHint__noAccounts: '目前無可用的外部帳戶供應商。',
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法再使用此已連結帳戶，任何依賴此連結的功能也將失效。',
        successMessage: '{{connectedAccount}} 已從您的帳戶中移除。',
        title: '移除已連結的帳戶',
      },
      socialButtonsBlockButton: '連結 {{provider|titleize}} 帳戶',
      successMessage: '供應商已新增至您的帳戶',
      title: '新增已連結的帳戶',
    },
    deletePage: {
      actionDescription: '請在下方輸入「Delete account」以繼續。',
      confirm: '刪除帳戶',
      messageLine1: '您確定要刪除您的帳戶嗎？某些相關資料可能會被保留。如需申請完全刪除資料，請聯絡支援團隊。',
      messageLine2: '此動作將永久生效且無法復原。',
      title: '刪除帳戶',
    },
    emailAddressPage: {
      emailCode: {
        formHint: '系統將會傳送一封包含驗證碼的郵件至此電子郵件地址。',
        formSubtitle: '請輸入傳送至 {{identifier}} 的驗證碼',
        formTitle: '驗證碼',
        resendButton: '重新傳送驗證碼',
        successMessage: '電子郵件 {{identifier}} 已新增至您的帳戶。',
      },
      emailLink: {
        formHint: '系統將會傳送一封包含驗證連結的郵件至此電子郵件地址。',
        formSubtitle: '請點擊傳送至 {{identifier}} 的郵件中的驗證連結',
        formTitle: '驗證連結',
        resendButton: '重新傳送連結',
        successMessage: '電子郵件 {{identifier}} 已新增至您的帳戶。',
      },
      enterpriseSSOLink: {
        formButton: '點擊以登入',
        formSubtitle: '完成與 {{identifier}} 的登入',
      },
      formHint: '您需要驗證此電子郵件地址才能將其新增至您的帳戶。',
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法再使用此電子郵件地址登入。',
        successMessage: '電子郵件 {{emailAddress}} 已從您的帳戶中移除。',
        title: '移除電子郵件地址',
      },
      title: '新增電子郵件地址',
      verifyTitle: '驗證電子郵件地址',
    },
    formButtonPrimary__add: '新增',
    formButtonPrimary__continue: '繼續',
    formButtonPrimary__finish: '完成',
    formButtonPrimary__remove: '移除',
    formButtonPrimary__save: '儲存',
    formButtonReset: '取消',
    mfaPage: {
      formHint: '選擇一種新增方式。',
      title: '新增兩步驟驗證',
    },
    mfaPhoneCodePage: {
      backButton: '使用現有號碼',
      primaryButton__addPhoneNumber: '新增電話號碼',
      removeResource: {
        messageLine1: '{{identifier}} 將不再用於接收登入驗證碼。',
        messageLine2: '您的帳戶安全性可能會降低，確定要繼續嗎？',
        successMessage: '已移除 {{mfaPhoneCode}} 的簡訊兩步驟驗證',
        title: '移除兩步驟驗證',
      },
      subtitle__availablePhoneNumbers: '選擇一個電話號碼以註冊簡訊兩步驟驗證。',
      subtitle__unavailablePhoneNumbers: '目前沒有可用的電話號碼以註冊簡訊兩步驟驗證。',
      successMessage1: '登入時，您需要輸入傳送到此電話號碼的驗證碼，作為額外的驗證步驟。',
      successMessage2: '請妥善儲存這些備用碼。若您無法存取驗證裝置，可以使用備用碼登入。',
      successTitle: '簡訊驗證已啟用',
      title: '新增簡訊驗證',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: '掃描 QR Code',
        buttonUnableToScan__nonPrimary: '無法掃描 QR Code？',
        infoText__ableToScan: '在您的驗證器應用程式中設定新的登入方式，並掃描下方的 QR Code 以連結您的帳戶。',
        infoText__unableToScan: '在驗證器中設定新的登入方式，並輸入下方提供的金鑰 (Key)。',
        inputLabel__unableToScan1: '請確認已啟用 TOTP (基於時間的一次性密碼)，然後完成帳戶連結。',
        inputLabel__unableToScan2: '或者，若您的驗證器支援 TOTP URI，您也可以複製完整的 URI。',
      },
      removeResource: {
        messageLine1: '登入時將不再需要輸入此驗證器的驗證碼。',
        messageLine2: '您的帳戶安全性可能會降低，確定要繼續嗎？',
        successMessage: '已移除透過驗證器應用程式的兩步驟驗證。',
        title: '移除兩步驟驗證',
      },
      successMessage: '兩步驟驗證現已啟用。登入時，您需要輸入此驗證器產生的驗證碼作為額外步驟。',
      title: '新增驗證器應用程式',
      verifySubtitle: '請輸入您的驗證器產生的驗證碼',
      verifyTitle: '驗證碼',
    },
    mobileButton__menu: '選單',
    navbar: {
      account: '個人資料',
      apiKeys: 'API 金鑰',
      billing: '結帳',
      description: '管理您的帳戶資訊。',
      security: '安全性',
      title: '帳戶',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} 將從此帳戶中被移除。',
        title: '移除金鑰',
      },
      subtitle__rename: '您可以更改金鑰名稱，使其更容易找到。',
      title__rename: '重新命名金鑰',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions: '建議您登出所有可能使用過舊密碼的其他裝置。',
      readonly: '您目前無法編輯密碼，因為您只能透過企業連線登入。',
      successMessage__set: '您的密碼已設定。',
      successMessage__signOutOfOtherSessions: '已登出所有其他裝置。',
      successMessage__update: '您的密碼已更新。',
      title__set: '設定密碼',
      title__update: '變更密碼',
    },
    phoneNumberPage: {
      infoText: '系統將會傳送一封包含驗證連結的簡訊至此電話號碼。',
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法再使用此電話號碼登入。',
        successMessage: '電話號碼 {{phoneNumber}} 已從您的帳戶中移除。',
        title: '移除電話號碼',
      },
      successMessage: '{{identifier}} 已新增至您的帳戶。',
      title: '新增電話號碼',
      verifySubtitle: '請輸入傳送至 {{identifier}} 的驗證碼',
      verifyTitle: '驗證電話號碼',
    },
    plansPage: {
      title: '方案',
    },
    profilePage: {
      fileDropAreaHint: '上傳 JPG, PNG, GIF, 或 WEBP 格式圖片（檔案大小上限 10MB）',
      imageFormDestructiveActionSubtitle: '移除圖片',
      imageFormSubtitle: '上傳圖片',
      imageFormTitle: '個人資料圖片',
      readonly: '您的個人資料由企業連線提供，無法編輯。',
      successMessage: '您的個人資料已更新。',
      title: '更新個人資料',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: '登出裝置',
        title: '已登入的裝置',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: '再試一次',
        actionLabel__reauthorize: '立即重新授權',
        destructiveActionTitle: '移除',
        primaryButton: '連結帳戶',
        subtitle__disconnected: '此帳戶已斷開連接。',
        subtitle__reauthorize: '必要的權限範圍已更新，您可能會遇到功能受限的情況。請重新授權此應用程式以避免任何問題。',
        title: '已連結的帳戶',
      },
      dangerSection: {
        deleteAccountButton: '刪除帳戶',
        title: '危險',
      },
      emailAddressesSection: {
        destructiveAction: '移除電子郵件地址',
        detailsAction__nonPrimary: '設為主要',
        detailsAction__primary: '完成驗證',
        detailsAction__unverified: '完成驗證',
        primaryButton: '新增電子郵件地址',
        title: '電子郵件地址',
      },
      enterpriseAccountsSection: {
        primaryButton: '連結帳戶',
        title: '企業帳戶',
      },
      headerTitle__account: '帳戶',
      headerTitle__security: '安全性',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: '重新產生代碼',
          headerTitle: '備用碼',
          subtitle__regenerate: '取得一組新的安全備用碼。先前產生的備用碼將失效。',
          title__regenerate: '重新產生備用碼',
        },
        phoneCode: {
          actionLabel__setDefault: '設為預設',
          destructiveActionLabel: '移除電話號碼',
        },
        primaryButton: '新增兩步驟驗證',
        title: '兩步驟驗證',
        totp: {
          destructiveActionTitle: '移除',
          headerTitle: '驗證器應用程式',
        },
      },
      passkeysSection: {
        menuAction__destructive: '移除',
        menuAction__rename: '重新命名',
        primaryButton: '新增金鑰',
        title: '金鑰',
      },
      passwordSection: {
        primaryButton__setPassword: '設定密碼',
        primaryButton__updatePassword: '變更密碼',
        title: '密碼',
      },
      phoneNumbersSection: {
        destructiveAction: '移除電話號碼',
        detailsAction__nonPrimary: '設為主要',
        detailsAction__primary: '完成驗證',
        detailsAction__unverified: '完成驗證',
        primaryButton: '新增電話號碼',
        title: '電話號碼',
      },
      profileSection: {
        primaryButton: '更新個人資料',
        title: '個人資料',
      },
      usernameSection: {
        primaryButton__setUsername: '設定使用者名稱',
        primaryButton__updateUsername: '變更使用者名稱',
        title: '使用者名稱',
      },
      web3WalletsSection: {
        destructiveAction: '移除錢包',
        detailsAction__nonPrimary: '設為主要',
        primaryButton: '新增 Web3 錢包',
        title: 'Web3 錢包',
        web3SelectSolanaWalletScreen: {
          subtitle: '選擇一個 Solana 錢包以連線到您的帳戶。',
          title: '新增 Solana 錢包',
        },
      },
    },
    usernamePage: {
      successMessage: '您的使用者名稱已更新。',
      title__set: '設定使用者名稱',
      title__update: '變更使用者名稱',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} 將從此帳戶中被移除。',
        messageLine2: '您將無法再使用此 web3 錢包登入。',
        successMessage: '{{web3Wallet}} 已從您的帳戶中移除。',
        title: '移除 Web3 錢包',
      },
      subtitle__availableWallets: '選擇一個 web3 錢包以連結您的帳戶。',
      subtitle__unavailableWallets: '目前沒有可用的 web3 錢包。',
      successMessage: '錢包已新增至您的帳戶。',
      title: '新增 web3 錢包',
      web3WalletButtonsBlockButton: undefined,
    },
  },
  waitlist: {
    start: {
      actionLink: '登入',
      actionText: '已有存取權限？',
      formButton: '加入候補名單',
      subtitle: '輸入您的電子郵件地址，我們會在您的位置準備好時通知您',
      title: '加入候補名單',
    },
    success: {
      message: '您將很快被重新導向...',
      subtitle: '當您的位置準備好時，我們會與您聯繫',
      title: '感謝您加入候補名單！',
    },
  },
  web3SolanaWalletButtons: {
    connect: '使用 {{walletName}} 連線',
    continue: '使用 {{walletName}} 繼續',
    noneAvailable:
      '未偵測到 Solana Web3 錢包。請安裝支援 Web3 的 {{ solanaWalletsLink || link("wallet extension") }}。',
  },
} as const;
